import React, { useEffect, useState } from 'react';
import { AlertCircle, Undo2, X } from 'lucide-react';
import type { MovimientoStock } from '../services/ingredientesService';

/**
 * Deshacer una salida de bodega (PROMPT_N°051).
 *
 * No borra nada: el backend compensa con un movimiento de entrada, así que
 * el historial conserva las dos filas. Por eso el motivo es obligatorio —
 * es lo único que explica, seis meses después, por qué hay una salida y su
 * reverso pegados.
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  movimiento: MovimientoStock | null;
  nombreIngrediente: string;
  onConfirm: (motivo: string) => Promise<void>;
  isLoading?: boolean;
}

const ModalRevertirSalida: React.FC<Props> = ({
  isOpen,
  onClose,
  movimiento,
  nombreIngrediente,
  onConfirm,
  isLoading = false,
}) => {
  const [motivo, setMotivo] = useState('');
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMotivo('');
      setConfirmando(false);
    }
  }, [isOpen]);

  if (!isOpen || !movimiento) return null;

  const cantidad = Math.abs(movimiento.cantidad);
  const unidad = movimiento.unidad_medida;
  const puedeConfirmar = motivo.trim().length >= 3 && !isLoading;

  // `notas` viene como "<Motivo>: <destino> - <notas>" (ver el endpoint de
  // salida). Se muestra tal cual: es el destino original que el usuario
  // necesita reconocer para saber si ésta es la salida que quiere deshacer.
  const detalleOriginal = movimiento.notas?.trim();

  const handleConfirm = async () => {
    if (!puedeConfirmar) return;
    await onConfirm(motivo.trim());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="border-b dark:border-gray-700 px-6 py-4 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 p-2">
              <Undo2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Deshacer salida
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {nombreIngrediente}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            disabled={isLoading}
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Qué se va a deshacer */}
          <dl className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-600 dark:text-gray-400">Cantidad</dt>
              <dd className="font-semibold text-gray-900 dark:text-white">
                {cantidad} {unidad}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-600 dark:text-gray-400">Lote</dt>
              <dd className="font-mono text-xs text-gray-900 dark:text-white">
                {movimiento.lote_id ? `#${movimiento.lote_id}` : 'sin lote'}
              </dd>
            </div>
            {detalleOriginal && (
              <div className="flex justify-between gap-4">
                <dt className="text-gray-600 dark:text-gray-400 shrink-0">
                  Salida original
                </dt>
                <dd className="text-right text-gray-900 dark:text-white">
                  {detalleOriginal}
                </dd>
              </div>
            )}
          </dl>

          <p className="text-sm text-gray-600 dark:text-gray-300">
            La salida no se borra: queda en el historial con una entrada que
            le devuelve {cantidad} {unidad} al mismo lote.
          </p>

          {/* Motivo, obligatorio */}
          <div>
            <label
              htmlFor="motivo-reverso"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Motivo del reverso *
            </label>
            <textarea
              id="motivo-reverso"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              maxLength={500}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ej: se cargaron 100 g en vez de 10"
              disabled={isLoading}
            />
            <div className="mt-1 flex items-center justify-between">
              {motivo.trim().length > 0 && motivo.trim().length < 3 ? (
                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                  <AlertCircle size={14} />
                  Escribí al menos 3 caracteres
                </p>
              ) : (
                <span />
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {motivo.length}/500
              </p>
            </div>
          </div>

          {confirmando ? (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-3">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                ¿Devolver {cantidad} {unidad} a {nombreIngrediente}?
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmando(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  disabled={isLoading}
                >
                  No, volver
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? 'Deshaciendo…' : 'Sí, deshacer'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => setConfirmando(true)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={!puedeConfirmar}
              >
                <Undo2 className="h-4 w-4" />
                Deshacer salida
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalRevertirSalida;
